import Add from "@mui/icons-material/Add";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import { Box, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { SURFACE } from "../../theme/tokens";
import type { HppdBreakdownEntry } from "../../utils/hppdBreakdown";
import type { Job } from "../../utils/models";
import { QuickAddNurseDialog } from "./QuickAddNurseDialog";

type CensusGroupHeaderStat = {
	label: string;
	value?: number | string;
};

type CensusGroupHeaderRoleRatio = {
	role: string;
	current: number;
	target?: number;
};

type CensusGroupHeaderJobRatio = {
	jobId: string;
	jobDisplayName: string;
	current: number;
	target?: number;
};

/**
 * Build the role-ratio chips for a census group: the UNION of roles that have a target OR a count,
 * so a group with staff but no required (e.g. Unassigned) still renders its count. `target` is left
 * undefined when there is no requirement, so the header shows a bare count instead of "x / 0".
 * Shared by the daily table (NursesTableBody) and the gaps view (DayViewGaps) so they stay in sync.
 */
export function buildRoleRatios(group: { roleGroupRatios: Record<string, number>; roleGroupCounts: Record<string, number> }): CensusGroupHeaderRoleRatio[] {
	return Array.from(new Set([...Object.keys(group.roleGroupRatios), ...Object.keys(group.roleGroupCounts)]))
		.sort((a, b) => a.localeCompare(b))
		.map((role) => ({ role, current: group.roleGroupCounts[role] || 0, target: group.roleGroupRatios[role] }));
}

/** Job-ratio chips for a census group — same union semantics as buildRoleRatios, sorted by job display name. */
export function buildJobRatios(
	group: { jobGroupRatios: Record<string, number>; jobGroupCounts: Record<string, number> },
	jobs: Job[] | undefined,
): CensusGroupHeaderJobRatio[] {
	const displayName = (jobId: string) => jobs?.find((j) => j.id === jobId)?.displayName ?? jobId;
	return Array.from(new Set([...Object.keys(group.jobGroupRatios), ...Object.keys(group.jobGroupCounts)]))
		.sort((a, b) => displayName(a).localeCompare(displayName(b)))
		.map((jobId) => ({ jobId, jobDisplayName: displayName(jobId), current: group.jobGroupCounts[jobId] || 0, target: group.jobGroupRatios[jobId] }));
}

type CensusGroupHeaderContentProps = {
	name: string;
	subtext?: string;
	locationKey?: string;
	isCollapsed: boolean;
	date?: DateTime;
	showActual?: boolean;
	stats?: CensusGroupHeaderStat[];
	roleRatios?: CensusGroupHeaderRoleRatio[];
	jobRatios?: CensusGroupHeaderJobRatio[];
	hppdBreakdown?: HppdBreakdownEntry[];
	totalHppd?: number;
	selectedTimeRange?: { start: DateTime; end: DateTime };
	toggleGroupExpand: () => void;
};

const pipeSeparatorSx = { "&::before": { content: "'|'", mx: 0.5 } } as const;
const pipeSeparatorRatioSx = { "&::before": { content: "'|'", ml: 0.5, mr: 1 } } as const;

const showAddButton = (props: CensusGroupHeaderContentProps): props is CensusGroupHeaderContentProps & { date: DateTime; locationKey: string } =>
	props.date != null && props.locationKey != null;

export const CensusGroupHeaderContent: FC<CensusGroupHeaderContentProps> = (props) => {
	const { name, subtext, isCollapsed, stats, roleRatios, jobRatios, hppdBreakdown, totalHppd, selectedTimeRange, toggleGroupExpand } = props;
	const { track } = useAnalytics();
	const [quickAddOpen, setQuickAddOpen] = useState(false);
	const canAdd = showAddButton(props);

	return (
		<>
			<Box
				onClick={(_) => toggleGroupExpand()}
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 0.5,
					cursor: "pointer",
					bgcolor: "primary.dark",
					color: SURFACE,
					fontSize: "14px",
					px: 1.25,
					py: 0.5,
					position: "sticky",
					top: 0,
					zIndex: 10,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center" }}>{isCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}</Box>
				<Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<Box component="span" sx={{ display: "flex", flexDirection: "column", width: "11rem" }}>
						<Typography component="span" sx={{ fontWeight: 600 }}>
							{name}
						</Typography>
						{subtext && (
							<Typography component="span" sx={{ fontWeight: 400, fontSize: "11px", opacity: 0.75, lineHeight: 1.1 }}>
								{subtext}
							</Typography>
						)}
					</Box>
					{stats?.map((stat) => (
						<Typography key={stat.label} component="span" sx={{ fontWeight: 500, ...pipeSeparatorSx }}>
							{stat.value !== undefined && (
								<Box component="span" sx={{ mx: 1, fontWeight: 600 }}>
									{stat.value}
								</Box>
							)}
							{stat.label}
						</Typography>
					))}
					{roleRatios?.map(({ role, current, target }) => (
						<Typography key={role} component="span" sx={{ fontWeight: 500, ...pipeSeparatorRatioSx }}>
							{role}:
							<Typography component="span" sx={{ ml: 1, fontWeight: 600 }}>
								{current}
								{target != null ? ` / ${target}` : ""}
							</Typography>
						</Typography>
					))}
					{jobRatios?.map(({ jobId, jobDisplayName, current, target }) => (
						<Typography key={jobId} component="span" sx={{ fontWeight: 500, ...pipeSeparatorRatioSx }}>
							{jobDisplayName}:
							<Typography component="span" sx={{ ml: 1, fontWeight: 600 }}>
								{current}
								{target != null ? ` / ${target}` : ""}
							</Typography>
						</Typography>
					))}
					{hppdBreakdown?.map(({ key, hppd }) => (
						<Typography key={key} component="span" sx={{ fontWeight: 500, ...pipeSeparatorSx }}>
							{key}:
							<Box component="span" sx={{ mx: 0.5, fontWeight: 600 }}>
								{hppd.toFixed(2)}
							</Box>
						</Typography>
					))}
					{hppdBreakdown && totalHppd != null && (
						<Typography component="span" sx={{ fontWeight: 500, ...pipeSeparatorSx }}>
							Total HPPD:
							<Box component="span" sx={{ mx: 0.5, fontWeight: 600 }}>
								{totalHppd.toFixed(2)}
							</Box>
						</Typography>
					)}
				</Box>
				{canAdd && (
					<IconButton
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							track("census_group_add_nurse_clicked", { location_key: props.locationKey });
							setQuickAddOpen(true);
						}}
						sx={{ color: SURFACE, p: "2px", ml: "auto", "&:hover": { backgroundColor: alpha(SURFACE, 0.15) } }}
					>
						<Add sx={{ fontSize: "20px" }} />
					</IconButton>
				)}
			</Box>
			{quickAddOpen && canAdd && (
				<QuickAddNurseDialog
					date={props.date}
					showActual={props.showActual ?? false}
					sectionName={name}
					sectionLocationKey={props.locationKey}
					selectedTimeRange={selectedTimeRange}
					onClose={() => setQuickAddOpen(false)}
				/>
			)}
		</>
	);
};
